/**
 * API Client
 * Centralized client for making API requests with consistent error handling,
 * request formatting and response processing
 */

import { API_BASE, REQUEST_TIMEOUT, AI_API_BASE } from "./config";

// Track if the browser is currently navigating or refreshing
if (typeof window !== "undefined") {
  window.addEventListener("beforeunload", () => {
    (window as any).__isUnloading = true;
  });
}

interface ApiResponse<T = any> {
  message: any;
  status: number;
  data?: T;
  error?: string;
  page?: any;
}

interface RequestOptions {
  credentials?: RequestCredentials;
  method?: "GET" | "POST" | "PUT" | "DELETE";
  headers?: Record<string, string>;
  body?: any;
  timeout?: number;
}

function formatResponse<T>(data: any, response: { status: number }, successMessage: string) {
  // Check if data already follows the wrapper format (contains data and page)
  const isPaginated = Boolean(data && "data" in data && "page" in data);

  let result: Record<string, any>;

  if (isPaginated) {
    const { data: innerData, page, ...rest } = data;
    result = {
      data: innerData,
      page,
      ...rest,
    };
  } else {
    // Direct object payload (like your user object)
    result = {
      data: data ?? null,
    };
  }

  return {
    status: response.status,
    message: successMessage,
    ...result,
  };
}

/**
 * Get stored authentication tokens (Fallback for production)
 */
const getAuthTokens = () => {
  if (typeof window === "undefined") return { accessToken: null, refreshToken: null };
  const accessToken = localStorage.getItem("jwt");
  const refreshToken = localStorage.getItem("refreshToken");
  return { accessToken, refreshToken };
};

/**
 * Store authentication tokens
 * Deprecated: Tokens are now handled via HttpOnly cookies
 */
const storeAuthTokens = () => {
  // Tokens are set via HttpOnly cookies by the backend
};

const apiAuthRequest = async (
  endpoint: string,
  options: RequestOptions = {},
  base: "api" | "ai" = "api",
): Promise<RequestOptions> => {
  const { accessToken } = getAuthTokens();

  const authOptions: RequestOptions & RequestInit = {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      ...(options.headers || {}),
    },
    credentials: base === "ai" ? "omit" : "include", // Don't include cookies for AI backend to avoid CORS errors
  };
  return authOptions;
};

/**
 * Make an API request with consistent error handling
 */
export const apiRequest = async <T = any>(
  endpoint: string,
  options: RequestOptions = {},
  requestType: string = "",
  nosignal = false,
  base: "api" | "ai" = "api",
): Promise<ApiResponse<T>> => {
  const baseUrl = base === "api" ? API_BASE : AI_API_BASE;

  if (requestType === "authenticated") {
    const authOptions = await apiAuthRequest(endpoint, options, base);
    options = { ...authOptions };
  } else {
    // Make sure public requests also include credentials (except AI backend)
    (options as any).credentials = base === "ai" ? "omit" : "include";
  }

  const requestOptions: RequestOptions & RequestInit = {
    method: options.method || "GET",
    headers: {
      "Content-Type": "application/json",
      "Accept-Encoding": "gzip",
      Accept: "application/json",
      ...options.headers,
    },
    credentials: options.credentials || (base === "ai" ? "omit" : "include"),
  };

  if (options.body) {
    requestOptions.body =
      typeof options.body === "string"
        ? options.body
        : JSON.stringify({ ...options.body });
  }

  if (!nosignal) {
    requestOptions.timeout = options.timeout || REQUEST_TIMEOUT;
  }

  try {
    const url = endpoint.startsWith("http") ? endpoint : `${baseUrl}${endpoint}`;

    const executeFetch = async (urlToFetch: string, fetchOptions: RequestInit) => {
      const controller = new AbortController();
      const id = setTimeout(() => controller.abort(), requestOptions.timeout);

      try {
        const response = await fetch(urlToFetch, {
          ...fetchOptions,
          signal: nosignal ? undefined : controller.signal,
          keepalive: nosignal
        });
        clearTimeout(id);
        return response;
      } catch (err) {
        clearTimeout(id);
        throw err;
      }
    };

    let response = await executeFetch(url, requestOptions);

    // Handle token refresh
    if (response.status === 401 && requestType === "authenticated") {
      const refreshResponse = await fetch(`${baseUrl}/user/refresh-token`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      });

      if (refreshResponse.ok) {
        // Retry original request
        response = await executeFetch(url, requestOptions);
      } else {
        // Refresh failed, could trigger logout here
        console.error("Refresh token failed");
      }
    }

    let data: any = null;
    let rawText = "";
    try {
      if (!response.bodyUsed) {
        rawText = await response.text(); // ✅ only read once
        try {
          data = rawText ? JSON.parse(rawText) : {};
        } catch {
          data = { message: rawText };
        }
      } else {
        console.warn("Response body already used for URL:", url);
      }
    } catch (e) {
      console.error("Failed to read response body:", e);
      data = { message: "Failed to read response" };
    }

    if (!response.ok) {
      let errorMessage = "An error occurred";
      if (typeof data === "string") {
        errorMessage = data;
      } else if (data && typeof data === "object") {
        if (typeof data.message === "string" && data.message) errorMessage = data.message;
        else if (typeof data.error === "string" && data.error) errorMessage = data.error;
        else if (Object.keys(data).length > 0) errorMessage = JSON.stringify(data);
      }

      return {
        status: response.status,
        message: errorMessage,
        error: errorMessage,
      };
    }

    let successMessage = "Success";
    if (data && typeof data.message === "string" && data.message) {
      successMessage = data.message;
    } else if (data && typeof data.message === "object") {
      successMessage = JSON.stringify(data.message);
    }

    return formatResponse<T>(data, response, successMessage);
  } catch (error: any) {
    const isUnloading = typeof window !== "undefined" && (window as any).__isUnloading;
    const isBrowserAbort = error?.name === "TypeError" && error?.message === "Failed to fetch";

    if (isUnloading || isBrowserAbort) {
      console.warn(`[API Cancelled] ${endpoint}: Navigation or refresh detected.`);
    } else if (error.name === "AbortError") {
      console.error(`[API Timeout] ${endpoint}: Request took longer than ${REQUEST_TIMEOUT}ms`);
      return {
        status: 408,
        message: "Request timeout",
        error: "Request timeout",
      };
    } else {
      console.error(`API Request Error (${endpoint}):`, error);
    }

    // Return a never-resolving promise if unloading to prevent state updates on unmounted components
    if (isUnloading) {
      return new Promise(() => { });
    }

    return {
      status: 500,
      message: "Network Error",
      error: error.message || "Network Error",
    };
  }
};

/**
 * Helper function for GET requests
 */
export const apiGet = async <T = any>(
  endpoint: string,
  options: Omit<RequestOptions, "method"> = {},
  requestType: string = "",
  nosignal = false,
  base: "api" | "ai" = "api",
): Promise<ApiResponse<T>> => {
  return apiRequest<T>(
    endpoint,
    { ...options, method: "GET" },
    requestType,
    nosignal,
    base,
  );
};

/**
 * Helper function for POST requests
 */
export const apiPost = async <T = any>(
  endpoint: string,
  body: any,
  options: Omit<RequestOptions, "method" | "body"> = {},
  requestType: string = "",
  nosignal = false,
  base: "api" | "ai" = "api",
): Promise<ApiResponse<T>> => {
  return apiRequest<T>(
    endpoint,
    { ...options, body, method: "POST" },
    requestType,
    nosignal,
    base,
  );
};

/**
 * Helper function for PUT requests
 */
export const apiPut = async <T = any>(
  endpoint: string,
  body: any,
  options: Omit<RequestOptions, "method" | "body"> = {},
  requestType: string = "",
  nosignal = false,
  base: "api" | "ai" = "api",
): Promise<ApiResponse<T>> => {
  return apiRequest<T>(
    endpoint,
    { ...options, body, method: "PUT" },
    requestType,
    nosignal,
    base,
  );
};

/**
 * Helper function for DELETE requests
 */
export const apiDelete = async <T = any>(
  endpoint: string,
  options: Omit<RequestOptions, "method"> = {},
  requestType: string = "",
  nosignal = false,
  base: "api" | "ai" = "api",
): Promise<ApiResponse<T>> => {
  return apiRequest<T>(
    endpoint,
    { ...options, method: "DELETE" },
    requestType,
    nosignal,
    base,
  );
};
